import type { ChangeEvent } from 'react';
import { useCallback } from 'react';

import { Button } from 'design-system';
import { Share } from 'lucide-react';

import { toInlineStyle } from '@/features/_shared/lib/legacyStyle';
import ShareDialog from '@/features/_shared/ShareDialog';

import useShareButton from './useShareButton';

const buttonBaseStyle = { width: 192, height: 48, fontSize: '16px' };

const sizeMap = {
  compact: 'sm',
  small: 'sm',
  medium: 'default',
  large: 'lg'
} as const;

const ShareButton = ({
  title,
  additionalTitle,
  shareUrl: shareUrlOverride,
  sx,
  iconSize = 24,
  size
}: {
  title?: string;
  additionalTitle?: string;
  shareUrl?: string;
  sx?: unknown;
  iconSize?: number;
  size?: keyof typeof sizeMap;
}) => {
  const {
    modalOpen,
    setModalOpen,
    content,
    setContent,
    recipient,
    handleChangeRecipient,
    handleCopyToClipboard,
    sendEmail,
    emailError,
    shareUrl
  } = useShareButton(undefined, shareUrlOverride);
  const headerSuffix = additionalTitle ? ` / ${additionalTitle}` : '';
  const header = `Share ${title}${headerSuffix}`;

  const handleClose = useCallback(() => {
    setModalOpen(false);
  }, [setModalOpen]);

  const handleContentChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setContent(e.target.value);
    },
    [setContent]
  );

  return (
    <>
      <Button
        size={size ? sizeMap[size] : 'lg'}
        style={{ ...(size ? { width: 192 } : buttonBaseStyle), ...toInlineStyle(sx) }}
        variant='secondary'
        onClick={() => {
          setModalOpen(true);
        }}
      >
        <Share style={{ width: iconSize, height: iconSize }} />
        Share
      </Button>
      <ShareDialog
        content={content}
        emailError={emailError}
        handleChangeRecipient={handleChangeRecipient}
        header={header}
        open={modalOpen}
        recipient={recipient}
        shareUrl={shareUrl}
        onClose={handleClose}
        onContentChange={handleContentChange}
        onCopyClick={handleCopyToClipboard}
        onSendEmail={sendEmail}
      />
    </>
  );
};

export default ShareButton;
